import React from 'react';
import { toast } from "react-toastify";
import { AppConstants, OpiApi, PlatformApi } from '../../configs';
import { AxiosAuth } from '../../core';
import { ApiResponse } from '../../models';
import {
  CallRequestModel,
  SendOutboundDtmfRequestModel,
  CallUpdateRequestModel,
  ConferenceAddOperator,
  TranscriptionVisibilityResponseModel,
  ThreeWayCallReDialRequest,
} from '../../models/api/Call';

type CallIntakesUpdateRequest = {
  customInputRequestModels: Array<{
    criteriaType: number;
    value: string | null;
  }>;
};

export function useFunctionalityCalls() {
  const oncreate = React.useCallback(async (model: CallRequestModel) => {
    try {
      const response = await AxiosAuth.post<ApiResponse<string>>(
        //is this being used?
        PlatformApi.Call.Create(),
        model
      );
      if ((response.status === AppConstants.Api.StatusOK)) {
        return Promise.resolve(response.data.data);
      }
    } catch (e) {
      console.error(e);
      // return Promise.reject(e);
    }
  }, []);
  const onUpdate = React.useCallback(
    async (callId: string, model: CallUpdateRequestModel) => {
      try {
        const response = await AxiosAuth.put(
          PlatformApi.Call.Update(callId),
          model
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response.data);
        }
      } catch (e) {
        console.error(e);
        // return Promise.reject(e);
      }
    },
    []
  );
  const onStartInitiate = React.useCallback(
    async (callSid: string, deviceId: string) => {
      try {
        const response = await AxiosAuth.post(
          OpiApi.Operator.StartInitiated(),
          {
            deviceId: deviceId,
            callSid: callSid,
          }
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response.data.data);
        }
      } catch (e) {
        console.error(e);
        //return Promise.reject(e);
      }
    },
    []
  );
  const onCallConferenceParticipant = React.useCallback(
    async (data: ThreeWayCallReDialRequest) => {
      try {
        const response = await AxiosAuth.post(
          OpiApi.ThreeWayCall.CallConferenceParticipant(),
          data
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response);
        }
        return Promise.reject(new Error("Failed to join new conference"));
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    },
    []
  );

  const onSendOutboundDtmf = React.useCallback(
    async (data: SendOutboundDtmfRequestModel) => {
      try {
        const response = await AxiosAuth.post<ApiResponse<any>>(
          OpiApi.Operator.SendOutboundDtmf(),
          data
        );
        if (response.status === AppConstants.Api.StatusOK) {
          return Promise.resolve(response.data.data);
        }
        return Promise.reject(new Error("Failed to send outbound DTMF"));
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    },
    []
  );

  const onOutboundHangupLep = React.useCallback(
    async (callSid: string) => {
      try {
        const response = await AxiosAuth.post(
          PlatformApi.Conference.HangupOutboundLEP(callSid),
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response);
        }
      } catch (e) {
        console.error(e);
        //return Promise.reject(e);
      }
    },
    []
  );

  // const onOperatorOutboundRedial = React.useCallback(
  //   async (value: ConferenceAddOperator) => {

  //     const data = new URLSearchParams();
  //     data.append("callSid", value.callSid);
  //     data.append("called", value.called);
  //     data.append("from", value.from);

  //     try {
  //       const response = await AxiosAuth.post(OpiApi.ThreeWayCall.OutboundOperatorRedial(), data, {
  //         headers: {
  //           "Content-Type": "application/x-www-form-urlencoded"
  //         }
  //       });
  //       if ((response.status === AppConstants.Api.StatusOK)) {
  //         return Promise.resolve(response);
  //       }
  //     } catch (e) {
  //       console.error(e);
  //     }
  //   },
  //   []
  // );

  const onAddOperatorConferenceOPI = React.useCallback(
    async (value: ConferenceAddOperator) => {
      console.log("API Called: ", OpiApi.ThreeWayCall.AddOperatorConferenceOPI());

      try {
        const response = await AxiosAuth.post(
          OpiApi.ThreeWayCall.AddOperatorConferenceOPI(),
          value
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response);
        }
      } catch (e) {
        console.error(e);
      }
    },
    []
  );

  const onComplete = React.useCallback(async (callId: string) => {
    let endTime: string = new Date().toISOString();

    try {
      const response = await AxiosAuth.put(
        PlatformApi.Call.CompleteCall(callId, endTime)
      );
      if ((response.status === AppConstants.Api.StatusOK)) {
        return Promise.resolve(response.data.data);
      }
    } catch (e) {
      console.error(e);
      // return Promise.reject(e);
    }
  }, []);
  const onParticipantDisconnected = React.useCallback(
    async (callId: string, participantUserId: string) => {
      try {
        const response = await AxiosAuth.post(
          PlatformApi.Call.ParticipantDisconnected(callId, participantUserId)
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          return Promise.resolve(response.data.data);
        }
      } catch (e) {
        console.error(e);
        //return Promise.reject(e);
      }
    },
    []
  );

  const onUpdateIntakesAfterFinish = React.useCallback(
    async (callId: string, model: CallIntakesUpdateRequest) => {
      try {
        const response = await AxiosAuth.post<ApiResponse<any>>(
          PlatformApi.Call.UpdateIntakesAfterFinish(callId),
          model
        );
        if ((response.status === AppConstants.Api.StatusOK)) {
          toast.success("Intakes updated successfully");
          return Promise.resolve(response.data);
        }
        toast.error("Failed to update intakes");
      } catch (e) {
        toast.error("Failed to update intakes");
        console.error(e);
        // return Promise.reject(e);
      }
    },
    []
  );

  const onGetTranscriptionVisibility = React.useCallback(
    async (callSid: string, operatorUserId: string) => {
      try {
        const response = await AxiosAuth.get<ApiResponse<TranscriptionVisibilityResponseModel> | TranscriptionVisibilityResponseModel>(
          OpiApi.CallTracking.TranscriptionVisibility(callSid, operatorUserId)
        );
        if (response.status === AppConstants.Api.StatusOK) {
          const payload = (response.data as ApiResponse<TranscriptionVisibilityResponseModel>)?.data ??
            (response.data as TranscriptionVisibilityResponseModel);
          return Promise.resolve(payload);
        }
      } catch (e) {
        console.error(e);
      }
    },
    []
  );

  return {
    oncreate,
    onUpdate,
    onComplete,
    onParticipantDisconnected,
    onAddOperatorConferenceOPI,
    onStartInitiate,
    onCallConferenceParticipant,
    onSendOutboundDtmf,
    onOutboundHangupLep,
    onUpdateIntakesAfterFinish,
    onGetTranscriptionVisibility
  };
}
